import React, { useEffect, useMemo, useRef } from 'react'
import ReactDOM from 'react-dom'
import Snackbar from '@material-ui/core/Snackbar'
import { makeStyles, Slide } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import classNames from 'classnames'
import type { StyleClasses } from '../../styles'
import HorizontalScroller from '../HorizontalScroller'
import type { BulkActionButtonProps } from '../BulkActionButton'
import BulkActionButton from '../BulkActionButton'

const useStyles = makeStyles<Theme, BulkActionBarProps>(
  (theme) => ({
    root: {
      bottom: 0
    },
    belowBackdrop: {
      zIndex: 1300
    },
    snackbar: {
      height: theme.typography.pxToRem(60),
      width: '100vw',
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    },
    content: {
      margin: '0 auto',
      height: '100%',
      display: 'flex',
      flexDirection: 'row' as const,
      justifyContent: 'space-around',
      alignItems: 'center',
      width: '80vw',
      [theme.breakpoints.down('md')]: {
        width: '100vw'
      }
    },
    selectedLabelWrapper: {
      flexGrow: 1,
      width: theme.typography.pxToRem(175),
      display: 'flex',
      justifyContent: 'flex-start',
      '@media (max-width: 599px)': {
        display: 'none'
      }
    },
    selectedLabel: {
      ...theme.typography.body1,
      color: 'white',
      marginLeft: theme.spacing(1),
      padding: theme.spacing(0, 2)
    },
    horizontalScroller: {
      flexGrow: 2,
      overflowX: 'hidden'
    },
    scrollButton: {
      color: theme.palette.primary.contrastText
    },
    horizontalScrollerContent: {
      height: '100%',
      display: 'flex',
      flexDirection: 'row' as const,
      alignItems: 'center',
      // justifyContent as 'center' will cause the scroller to not show all elements on small screens
      // so using margin to center works when we use a div inside of HoriztonalScroller
      margin: '0 auto'
    },
    deselectWrapper: {
      flexGrow: 1,
      width: theme.typography.pxToRem(175),
      display: 'flex',
      justifyContent: 'flex-end',
      '@media (max-width: 599px)': {
        display: 'none'
      }
    }
  }),
  { name: 'BulkActionBar' }
)

export interface BulkActionBarProps {
  itemName: string
  pluralItemName?: string
  selectedItems?: any[]
  onDeselectAll?: () => void
  children?: Array<React.ReactElement<BulkActionButtonProps>> | React.ReactElement<BulkActionButtonProps>
  aboveBackdrop?: boolean
  classes?: StyleClasses<typeof useStyles>
}

function BulkActionBar(props: BulkActionBarProps) {
  const { itemName, pluralItemName, selectedItems, onDeselectAll, aboveBackdrop, children } = props
  const classes = useStyles(props)
  const rootDiv = useRef<HTMLDivElement>(document.createElement('div'))

  // Mount/unmount portal container
  useEffect(() => {
    document.body.appendChild(rootDiv.current)
    return () => {
      document.body.removeChild(rootDiv.current)
    }
  }, [])

  // Push Intercom behind the bar
  useEffect(() => {
    const openIntercom = document.querySelector<HTMLElement>('#intercom-container > div > iframe')
    const closedIntercom = document.querySelector<HTMLElement>('body > div.intercom-lightweight-app')
    if (openIntercom) openIntercom.style.zIndex = '1299'
    if (closedIntercom) closedIntercom.style.zIndex = '1299'
  }, [selectedItems])

  const handleActionButtonClick = (child: React.ReactElement<BulkActionButtonProps>) => {
    if (child?.props?.onClick) {
      child.props.onClick(selectedItems)
    }
  }

  const actionButtons = useMemo(
    () =>
      React.Children.map(
        children,
        (child: React.ReactElement<BulkActionButtonProps>) =>
          child &&
          React.cloneElement(child, {
            onClick: () => handleActionButtonClick(child)
          })
      ),
    [children, selectedItems]
  )

  const getSelectedLabel = () => {
    const numSelected = selectedItems ? selectedItems.length : 0
    let plural = pluralItemName

    if (!pluralItemName) {
      plural = itemName.toLowerCase().endsWith('s') ? `${itemName}'` : `${itemName}s`
    }

    return `${numSelected} ${numSelected === 1 ? itemName : plural} selected`
  }

  return ReactDOM.createPortal(
    <Snackbar
      classes={{
        root: classNames({
          [classes.root]: true,
          [classes.belowBackdrop]: !aboveBackdrop
        })
      }}
      open={!!(selectedItems && selectedItems.length > 0)}
      TransitionComponent={Slide}
    >
      <div className={classes.snackbar}>
        <div className={classes.content}>
          <div className={classes.selectedLabelWrapper}>
            <div className={classes.selectedLabel}>{getSelectedLabel()}</div>
          </div>
          <HorizontalScroller
            classes={{
              root: classes.horizontalScroller,
              scrollButton: classes.scrollButton
            }}
          >
            <div className={classes.horizontalScrollerContent}>{actionButtons}</div>
          </HorizontalScroller>
          <div className={classes.deselectWrapper}>
            <BulkActionButton onClick={onDeselectAll}>Deselect All</BulkActionButton>
          </div>
        </div>
      </div>
    </Snackbar>,
    rootDiv.current
  )
}

export default BulkActionBar
